import { Controller } from "@hotwired/stimulus"

// Queue name chips for JQL/JQS Collection.
// Always submits autoscaler[strategy_attributes][queues][] as an array.
// Zero chips → one empty-string hidden input (model strip → []).
// Commit on Enter/Tab when the input has pending text, or on comma.
// Empty Enter/Tab are not intercepted (form submit / focus move stay default).
// Spaces allowed inside a name. Chip labels use text nodes only.
export default class extends Controller {
  static targets = ["list", "input", "values"]
  static values = {
    paramName: String
  }

  connect() {
    this.boundSyncFromDom = this.syncFromDom.bind(this)
    document.addEventListener("turbo:morph", this.boundSyncFromDom)
    this.syncFromDom()
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.boundSyncFromDom)
  }

  keydown(event) {
    const pending = this.inputTarget.value.trim() !== ""

    if (event.key === ",") {
      event.preventDefault()
      this.commitInput()
    } else if ((event.key === "Enter" || event.key === "Tab") && pending) {
      event.preventDefault()
      this.commitInput()
    } else if (event.key === "Backspace" && this.inputTarget.value === "") {
      this.removeLastChip()
    }
  }

  blur() {
    this.commitInput()
  }

  remove(event) {
    const chip = event.currentTarget.closest("[data-queue-chip]")
    if (!chip) return
    const name = chip.dataset.queueName
    chip.remove()
    this.removeValue(name)
    this.ensureEmptyValueIfNeeded()
    this.inputTarget.focus()
  }

  commitInput() {
    const raw = this.inputTarget.value
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean)
    if (parts.length === 0) {
      this.inputTarget.value = ""
      return
    }

    for (const name of parts) {
      if (!this.hasChip(name)) this.addChip(name)
    }
    this.inputTarget.value = ""
    this.ensureEmptyValueIfNeeded()
  }

  syncFromDom() {
    // Server re-renders chips + hiddens on 422 / morph; list is source of truth.
    this.ensureEmptyValueIfNeeded()
  }

  hasChip(name) {
    return Array.from(this.listTarget.querySelectorAll("[data-queue-chip]")).some(
      (chip) => chip.dataset.queueName === name
    )
  }

  addChip(name) {
    this.clearPlaceholderValues()

    const chip = document.createElement("span")
    chip.dataset.queueChip = ""
    chip.dataset.queueName = name
    // Keep in sync with AutoscalersHelper::QUEUE_CHIP_CLASSES (show + form chips).
    chip.className =
      "inline-flex items-center gap-1 max-w-full rounded-md font-normal " +
      "bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs " +
      "text-gray-800 dark:text-gray-100"

    const label = document.createElement("span")
    label.className = "truncate"
    label.textContent = name
    chip.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className =
      "shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 " +
      "leading-none cursor-pointer"
    remove.setAttribute("aria-label", `Remove ${name}`)
    remove.dataset.action = "click->autoscaler--queues-chips#remove"
    remove.textContent = "×"
    chip.appendChild(remove)

    this.listTarget.appendChild(chip)
    this.valuesTarget.appendChild(this.buildHidden(name))
  }

  removeLastChip() {
    const chips = this.listTarget.querySelectorAll("[data-queue-chip]")
    if (chips.length === 0) return
    const last = chips[chips.length - 1]
    const name = last.dataset.queueName
    last.remove()
    this.removeValue(name)
    this.ensureEmptyValueIfNeeded()
  }

  removeValue(name) {
    this.hiddenInputs()
      .filter((input) => input.value === name)
      .forEach((input) => input.remove())
  }

  clearPlaceholderValues() {
    this.hiddenInputs()
      .filter((input) => input.value === "")
      .forEach((input) => input.remove())
  }

  ensureEmptyValueIfNeeded() {
    const inputs = this.hiddenInputs()
    const real = inputs.filter((input) => input.value !== "")
    const placeholders = inputs.filter((input) => input.value === "")

    if (real.length > 0) {
      placeholders.forEach((input) => input.remove())
      return
    }

    if (placeholders.length === 0) {
      this.valuesTarget.appendChild(this.buildHidden(""))
    } else if (placeholders.length > 1) {
      placeholders.slice(1).forEach((input) => input.remove())
    }
  }

  hiddenInputs() {
    return Array.from(this.valuesTarget.querySelectorAll("input[type='hidden']"))
  }

  buildHidden(value) {
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.paramNameValue
    hidden.value = value
    return hidden
  }
}
